// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Global board-state comparison (spec §3.7, §6.3).
 *
 * These blocks belong to no region and cannot be region-merged. A difference
 * here means the worker changed something that **will be lost** by a region
 * merge — so it is never folded into the object change list, and it is always
 * displayed, even when empty ("global board state: identical").
 */

import { serialize, structuralEqual, type Node } from '../sexp/index.ts';
import { GLOBAL_BLOCKS, HEADER_FIELDS } from '../model/objects.ts';
import type { Board } from '../model/board.ts';
import { diffFields, type FieldChange } from './fields.ts';
import { normalizeGlobalBlock } from './normalize.ts';

export type GlobalBlockStatus = 'identical' | 'changed' | 'only-in-a' | 'only-in-b' | 'absent';

export interface GlobalBlockDiff {
  block: string;
  status: GlobalBlockStatus;
  fields: FieldChange[];
}

export interface NetTableDiff {
  onlyInA: string[];
  onlyInB: string[];
  /** Same names, different indices — harmless in itself, fatal if ignored (§3.1). */
  reindexed: Array<{ name: string; indexA: number; indexB: number }>;
  identicalNames: boolean;
}

export interface GlobalDiff {
  blocks: GlobalBlockDiff[];
  nets: NetTableDiff;
  /** True when every global block and the net *name* set are identical. */
  identical: boolean;
}

/** Blocks compared individually, in the order the UI should display them. */
export const COMPARED_BLOCKS: readonly string[] = [...HEADER_FIELDS, ...GLOBAL_BLOCKS];

export function diffGlobal(a: Board, b: Board): GlobalDiff {
  const blocks: GlobalBlockDiff[] = [];

  for (const name of COMPARED_BLOCKS) {
    const an = a.globalBlocks.get(name);
    const bn = b.globalBlocks.get(name);
    if (an === undefined && bn === undefined) {
      blocks.push({ block: name, status: 'absent', fields: [] });
      continue;
    }
    if (an === undefined) {
      blocks.push({ block: name, status: 'only-in-b', fields: [] });
      continue;
    }
    if (bn === undefined) {
      blocks.push({ block: name, status: 'only-in-a', fields: [] });
      continue;
    }
    const na = normalizeGlobalBlock(an);
    const nb = normalizeGlobalBlock(bn);
    if (structuralEqual(na, nb)) {
      blocks.push({ block: name, status: 'identical', fields: [] });
    } else {
      blocks.push({ block: name, status: 'changed', fields: diffFields(na, nb, name) });
    }
  }

  // Board text variables are compared as part of `properties`, but KiCad also
  // writes them as flat board-level `(property …)` nodes. Compare that view too.
  const propFields = diffMaps(a.boardProperties, b.boardProperties, 'property');
  const existing = blocks.find((x) => x.block === 'properties');
  if (propFields.length > 0) {
    if (existing && existing.status === 'identical') {
      existing.status = 'changed';
      existing.fields = propFields;
    } else if (existing && existing.status === 'absent') {
      existing.status = 'changed';
      existing.fields = propFields;
    } else if (existing) {
      existing.fields = mergeFieldLists(existing.fields, propFields);
    }
  }

  const nets = diffNetTables(a, b);
  const identical = blocks.every((x) => x.status === 'identical' || x.status === 'absent') && nets.identicalNames;

  return { blocks, nets, identical };
}

function mergeFieldLists(a: FieldChange[], b: FieldChange[]): FieldChange[] {
  const seen = new Set(a.map((f) => `${f.path} ${f.before} ${f.after}`));
  const out = [...a];
  for (const f of b) {
    const k = `${f.path} ${f.before} ${f.after}`;
    if (!seen.has(k)) out.push(f);
  }
  return out;
}

function diffMaps(a: Map<string, string>, b: Map<string, string>, prefix: string): FieldChange[] {
  const out: FieldChange[] = [];
  const keys = [...new Set([...a.keys(), ...b.keys()])].sort();
  for (const k of keys) {
    const av = a.get(k);
    const bv = b.get(k);
    if (av === bv) continue;
    out.push({
      path: `${prefix}."${k}"`,
      ...(av !== undefined ? { before: av } : {}),
      ...(bv !== undefined ? { after: bv } : {}),
    });
  }
  return out;
}

/**
 * Compare net tables **by name** (spec §3.1, §6.3).
 *
 * Reindexing — the same names in a different order — is recorded because it is
 * exactly the situation that makes naive index-based splicing attach copper to
 * the wrong net. It is not itself a failure; ignoring it would be.
 */
export function diffNetTables(a: Board, b: Board): NetTableDiff {
  // `allNetNames` rather than the table: KiCad 10 has no table, and comparing
  // two empty tables would pass this check on every KiCad 10 board without
  // looking at a single net.
  const aNames = a.allNetNames();
  const bNames = b.allNetNames();
  const onlyInA = [...aNames].filter((n) => !bNames.has(n)).sort();
  const onlyInB = [...bNames].filter((n) => !aNames.has(n)).sort();

  const reindexed: NetTableDiff['reindexed'] = [];
  for (const name of [...aNames].sort()) {
    if (!bNames.has(name)) continue;
    const ia = a.netIndexByName.get(name);
    const ib = b.netIndexByName.get(name);
    if (ia !== undefined && ib !== undefined && ia !== ib) reindexed.push({ name, indexA: ia, indexB: ib });
  }

  return {
    onlyInA,
    onlyInB,
    reindexed,
    identicalNames: onlyInA.length === 0 && onlyInB.length === 0,
  };
}

/** Full text of a global block, for the UI's "show me the actual difference" view. */
export function blockText(board: Board, name: string): string | undefined {
  const block = board.globalBlocks.get(name);
  return block === undefined ? undefined : serialize(normalizeGlobalBlock(block) as Node);
}
